import React, {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
// state pour le tour guidé déclaré dans le composant
import {
  Grid, useMediaQuery, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import Actors from 'containers/layouts/mapPage/actors';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Drawer from '@mui/material/Drawer';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import dynamic from 'next/dynamic';
import ButtonGroupSelected from '../../components/buttons/ButtonGroupSelected';
import Filters from '../../components/filters';
import AppLayout from '../../containers/layouts/AppLayout';
import { withApollo } from '../../hoc/withApollo';
import FilterTutorial from '../../components/tutorial/FilterTutorial';
import MapAlertBanner from '../../components/notifications/MapAlertBanner';
import NotificationCTABanner from '../../components/notifications/NotificationCTABanner';
import GuidedTourOverlay, { GuidedTourStep } from '../../components/notifications/GuidedTourOverlay';

const MapWithNoSSR = dynamic(() => import('../../components/map/Map'), {
  ssr: false,
});

const MarkerWithNoSSR = dynamic(() => import('../../components/map/ActorMarker'), {
  ssr: false,
});

const MarkerClusterWithNoSSR = dynamic(() => import('../../components/map/MarkerCluster'), {
  ssr: false,
});

const drawerWidth = 310;

type StyleParams = {
  isMenuOpen: boolean;
  isMapMode: boolean;
};

const useStyles = makeStyles<StyleParams>()((theme, { isMenuOpen, isMapMode }) => ({
  '@media print': {
    drawer: {
      display: 'none',
    },
    filterButton: {
      display: 'none',
    },
    layout: {
      backgroundColor: 'white !important',
      display: 'block !important',
    },
  },
  layout: {
    display: 'flex',
    justifyContent: 'flex-start',
    backgroundColor: '#F6F6F6',
    padding: '0',
    margin: '0',
    width: '100%',
    maxWidth: 'none',
    position: 'relative',
    overflow: 'hidden',
    flex: 1,
    height: 'calc(100vh - 72px)',
    minHeight: 'calc(100vh - 72px)',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      height: 'auto',
      flex: 'none',
      width: '100%',
    },
  },
  drawer: {
    width: isMenuOpen ? drawerWidth : 0,
    flexShrink: 0,
    overflow: 'hidden',
    transition: isMapMode ? undefined : theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawerPaper: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: drawerWidth,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  filterButton: {
    position: 'absolute',
    left: isMenuOpen ? drawerWidth : 20,
    bottom: 20,
    zIndex: 1000,
    borderTopLeftRadius: isMenuOpen ? 0 : 4,
    borderBottomLeftRadius: isMenuOpen ? 0 : 4,
    transition: theme.transitions.create(['left'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      position: 'fixed',
      bottom: 10,
      left: 10,
    },
  },
  filterButtonIcon: {
    transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  },
  listButton: {
    position: 'absolute',
    bottom: 10,
    zIndex: 10000,
    color: '#fff',
    backgroundColor: '#2C367E',
    '&:hover': {
      color: '#2C367E',
      backgroundColor: '#fff',
    },
    [theme.breakpoints.down('md')]: {
      position: 'initial',
      marginTop: 25,
      width: '75%',
    },
  },
  listButtonIcon: {
    marginRight: 10,
  },
  gridList: {
    overflow: 'hidden auto',
  },
  leftTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(1),
    backgroundColor: '#F7F7F7',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  leftGridItem: {
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: '6px',
    width: '100%',
  },
  rightGridItem: {
    backgroundColor: '#FFFFFF',
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
    padding: theme.spacing(3),
    borderRadius: '6px',
    width: '100%',
  },
  topContainer: {
    padding: theme.spacing(4),
    maxWidth: '1120px',
  },
  leftTitles: {
    marginBottom: theme.spacing(10),
  },
  buyButton: {
    backgroundColor: '#009C95',
    width: '170px',
    borderRadius: '30px',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: '#FFFFFF',
  },
  price: {},
  sticky: {
    position: 'sticky',
    top: '100px',
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    boxShadow: '0 0 1px 1px rgba(20,23,28,.1), 0 3px 1px 0 rgba(20,23,28,.1)',
    padding: theme.spacing(3),
  },
  hightlightsBox: {
    padding: theme.spacing(2),
    backgroundColor: '#F9F9F9',
  },
  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  stickyFirstLine: {
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: '#bd0b3d',
    width: '20px',
  },
  datePicker: {
    border: '0.5px solid',
    borderRadius: '10px',
    padding: theme.spacing(2),
    width: '100%',
    color: '#B0B0B0',
    marginBottom: theme.spacing(2),
    '& label': {
      position: 'relative',
      fontWeight: '800',
    },
    '&.MuiInput-formControl': {
      margin: '0px',
    },
  },
  root: {
    minWidth: 120,
    width: '220px',
    padding: 'inherit',
    boxShadow: '0px 5px 26px -10px rgba(0, 0, 0, 0.46)',
    margin: '15px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
  },
  pos: {
    marginBottom: 12,
  },
  categorie: {
    backgroundColor: 'white',
    borderRadius: '0.3em',
    width: 'max-content',
    padding: '0 5px 0 5px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
  },
  image: {
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    textAlign: 'inherit',
    height: '10em',
  },
  title: {
    textAlign: 'left',
    color: '#2C367E',
    width: '100%',
  },
  content: {
    padding: '10px',
    width: '100%',
  },
  date: {
    textAlign: 'right',
    color: '#2C367E',
  },
  titleDiv: {
    display: 'flex',
    alignItems: 'center',
  },
  favorite: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  favoriteIcon: {
    color: '#2C367E;',
  },
  shortDescription: {
    wordBreak: 'break-word',
    textAlign: 'justify',
    width: '100%',
  },
  mapContainer: {
    // En desktop, une hauteur explicite évite les cas où la chaîne de parents en 100%
    // calcule à 0px et masque complètement la carte Leaflet.
    height: 'calc(100vh - 72px) !important',
    [theme.breakpoints.down('md')]: {
      height: '80vh !important',
    },
  },
}));

const categories = {
  Sujets: [] as string[],
};

const otherCategories: Record<string, string[]> = {
  "Territoire d'actions": [],
  "Statut d'acteur": [],
  'Public visé': [],
  'Collectif & réseaux': [],
};

const GET_ACTORS = gql`
  query actors($entries: [[String]], $search: String,$postCode: String,$favoritesForUser: String,$isValidated: Boolean,$actorsCollective: [[String]],$lat: Float,$lng: Float,$radius: Float) {
    actors(entries: $entries, search: $search,postCode: $postCode,favoritesForUser: $favoritesForUser,isValidated: $isValidated,actorsCollective:$actorsCollective,lat:$lat,lng:$lng,radius:$radius) {
      id
      name
      address
      city
      shortDescription
      lat
      lng
      url
      entries {
        label
        icon
        color
        description
        parentEntry {
          code
          label
          color
        }
      }
      favorites{
        id
      }
      pictures {
        id
        label
        originalPicturePath
        originalPictureFilename
        position
        logo
      }
    }
  }
`;

type ActorEntry = {
  label?: string;
  icon?: string;
  color?: string;
  description?: string;
  parentEntry?: {
    code?: string;
    label?: string;
    color?: string;
  };
};

type ActorPicture = {
  id: string;
  label?: string;
  originalPicturePath?: string;
  originalPictureFilename?: string;
  position?: number;
  logo?: boolean;
};

type ActorMapItem = {
  id: string;
  name: string;
  address?: string;
  city?: string;
  shortDescription?: string;
  lat?: number;
  lng?: number;
  url?: string;
  entries: ActorEntry[];
  favorites?: Array<{ id: string }>;
  pictures: ActorPicture[];
};

type GetActorsData = {
  actors: ActorMapItem[];
};

type ActorMapMarker = ActorMapItem & {
  lat: number;
  lng: number;
};

const FiltersComponent = Filters as React.ComponentType<{
  onFiltersChange: (filters: Record<string, unknown>) => void;
  closeHandler: () => void;
  isActorList?: boolean;
  inviteActor?: boolean;
  noEmailInviteActor?: boolean;
  onActiveFilterChange?: (filter: any | null) => void;
  saveDialogTrigger?: number;
}>;

const carto = () => {
  const mapRef = useRef<unknown>();
  const isFirstRef = useRef(true);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const { inviteActor, noEmailInviteActor } = router.query;
  const [tourOpen, setTourOpen] = useState(false);
  const [categoriesChecked, setCategoriesChecked] = useState(categories.Sujets);

  const [otherCategoriesChecked, setOtherCategoriesChecked] = useState(otherCategories);
  const [listMode, setListMode] = useState(true);
  const [postCode, setPostCode] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, unknown> | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(!matches);
  const [activeFilter, setActiveFilter] = useState<any | null>(null);
  const [saveDialogTrigger, setSaveDialogTrigger] = useState(0);
  useEffect(() => {
    if (router.query.openFilters === '1') {
      setIsMenuOpen(true);
    }
  }, [router.query.openFilters]);

  useEffect(() => {
    if (router.query.openTutorial === '1') {
      setIsMenuOpen(true);
      const t = setTimeout(() => {
        window.dispatchEvent(new Event('ouaaa:open-filter-tutorial'));
      }, 200);
      return () => clearTimeout(t);
    }
  }, [router.query.openTutorial]);

  const {
    data, refetch,
  } = useQuery<GetActorsData>(GET_ACTORS, {
    variables: {
      entries: [categoriesChecked],
      isValidated: true,
    },
  });

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const { classes: styles } = useStyles({ isMenuOpen, isMapMode: listMode });

  useEffect(() => {
    setIsMenuOpen(!matches);
  }, [matches]);

  const switchMode = useCallback(() => {
    setListMode(!listMode);
  }, [listMode]);

  useEffect(() => {
    const filterChange = () => {
      const newOtherCategoriesLists = Object.values(otherCategoriesChecked);
      const newEntries: string[][] = [categoriesChecked];

      newOtherCategoriesLists.forEach((otherCategoryList) => {
        if (otherCategoryList.length > 0) newEntries.push(otherCategoryList);
      });

      if (isFirstRef.current) {
        if (newEntries.length !== 0 || typeof postCode !== 'undefined') {
          isFirstRef.current = false;
        } else {
          return;
        }
      }
      refetch({ entries: newEntries, postCode, isValidated: true });
    };
    filterChange();
  }, [categoriesChecked, otherCategoriesChecked, postCode, refetch]);

  const handleFiltersChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters);
    const { locationLat, locationLng, locationRadius, locationLabel, ...rest } = newFilters;
    const variables: Record<string, unknown> = {
      entries: null,
      search: null,
      postCode: null,
      favoritesForUser: null,
      actorsCollective: null,
      lat: null,
      lng: null,
      radius: null,
      isValidated: true,
      ...rest,
    };
    if (locationLat != null && locationLng != null && locationRadius != null) {
      variables.lat = locationLat;
      variables.lng = locationLng;
      variables.radius = locationRadius;
    }
    refetch(variables);
  }, [refetch]);

  function splitWord(word: string | null, number: number): string {
    if (word != null) {
      const indexMax = Math.round(word.length / number);
      let wordSplit = '';
      if (indexMax > 1) {
        for (let i = 0; i < indexMax; i++) {
          wordSplit += word.slice(i * number, (i + 1) * number);
          if (i + 1 <= indexMax) {
            wordSplit += '<br><br> ';
          }
        }
        return wordSplit;
      }
      return word;
    }
    return '';
  }

  const otherCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, collectionLabel: string) => {
    const newOtherCategories = { ...otherCategoriesChecked };

    const otherCategoryId = e.target.value;
    const tempCollection = newOtherCategories[collectionLabel];

    const currentIndex = tempCollection.indexOf(otherCategoryId);

    if (currentIndex === -1) {
      tempCollection.push(otherCategoryId);
    } else {
      tempCollection.splice(currentIndex, 1);
    }

    setOtherCategoriesChecked(newOtherCategories);
  }, [otherCategoriesChecked]);

  const fabActions = useMemo(() => {
    return [
      { name: 'map', label: 'Carte', onClick: switchMode },
      { name: 'list', label: 'Liste', onClick: switchMode },
    ];
  }, [switchMode]);

  const actorsWithLocation = useMemo<ActorMapMarker[]>(() => {
    return (data?.actors || []).filter((actor): actor is ActorMapMarker => {
      return actor.lat != null && actor.lng != null;
    });
  }, [data]);

  const tourSteps: GuidedTourStep[] = [
    {
      target: '[data-tour="filter-panel"]',
      title: '1. Choisissez vos critères',
      body:
        "Sélectionnez ici les catégories, le lieu, le rayon ou les acteurs en favoris qui vous intéressent. La carte se filtre en direct.",
      placement: 'right',
      onEnter: () => {
        setIsMenuOpen(true);
      },
      dimSelectors: [
        '[data-tour="invite-actor"]',
        '[data-tour="suggest-event"]',
      ],
    },
    {
      target: '[data-tour="save-filter"]',
      title: '2. Enregistrez votre filtre',
      body:
        "Cliquez sur « Enregistrer » pour conserver ce filtre dans votre liste personnalisée.",
      placement: 'right',
    },
    {
      target: '[data-tour="notification-bell"]',
      title: "3. Activez les notifications",
      body:
        "Cliquez sur la cloche à côté de votre filtre pour recevoir un email ou une notification dès qu'un nouvel acteur correspondant arrive sur OUAAA.",
      placement: 'right',
    },
  ];

  return (
    <AppLayout hideFooter>
      <FilterTutorial onOpen={() => setIsMenuOpen(true)} />
      <NotificationCTABanner
        storageKey="carte"
        title="Découvrez les nouveaux acteurs de la transition près de chez vous"
        body="3 étapes pour ne plus rien rater : filtrer, enregistrer, activer."
        onActivateNotifications={() => setTourOpen(true)}
      />
      <Head>
        <title>Les acteurs de la transition citoyenne et écologique autour de la Rochelle, Aunis, Charente-Maritime</title>
        <meta
          name="description"
          content="Viens découvrir les acteurs agissant pour :  l'éducation, la culture, la santé, l'alimentation, la justice, l'économie, la citoyenneté, l'agriculture, l'industrie, l'habitat, la mobilité, l'énergie, le recyclage, la réduction des déchets, le climat, la qualité de l'air, la biodiversité, la gestion de l'eau, l'aménagement du territoire et d'autres sujets sur la transition citoyenne et écologique"
        />
      </Head>
      <Grid container className={styles.layout}>
        <ButtonGroupSelected buttons={fabActions} />

        <Drawer
          anchor="left"
          variant={matches ? 'temporary' : 'persistent'}
          open={isMenuOpen}
          className={styles.drawer}
          classes={{
            paper: styles.drawerPaper,
          }}
          onClose={toggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{ 'data-tour': 'filter-panel' } as any}
        >
          <FiltersComponent
            onFiltersChange={handleFiltersChange}
            closeHandler={toggleMenu}
            isActorList
            inviteActor={inviteActor === 'true'}
            noEmailInviteActor={noEmailInviteActor === 'true'}
            onActiveFilterChange={setActiveFilter}
            saveDialogTrigger={saveDialogTrigger}
          />
        </Drawer>

        {(!matches || !isMenuOpen) && (
          <Button
            variant="contained"
            className={styles.filterButton}
            onClick={toggleMenu}
            data-tour="filter-toggle"
            startIcon={<DoubleArrowIcon className={styles.filterButtonIcon} />}
            color="primary"
          >
            Filtres
          </Button>
        )}

        {listMode && (
          <Grid item xs={12} style={{ flex: 1, minWidth: 0, height: matches ? '80vh' : 'calc(100vh - 72px)', width: '100%' }}>
            <MapWithNoSSR scrollWheelZoom={true} classMap={styles.mapContainer}>
              <MarkerClusterWithNoSSR>
                {actorsWithLocation.map((actor) => (
                  <MarkerWithNoSSR key={actor.id} actor={actor as ActorMapMarker} />
                ))}
              </MarkerClusterWithNoSSR>
            </MapWithNoSSR>
          </Grid>
        )}
        {!listMode && (
          <Grid item xs={12} justifyContent="center" className={styles.gridList}>
            <Actors data={data} />
          </Grid>
        )}
        <MapAlertBanner
          filters={filters}
          hasActiveSavedFilter={!!activeFilter && !activeFilter.modified}
          onActivate={() => {
            setIsMenuOpen(true);
            setSaveDialogTrigger((v) => v + 1);
          }}
        />
      </Grid>
      <GuidedTourOverlay
        open={tourOpen}
        steps={tourSteps}
        onClose={() => setTourOpen(false)}
      />
    </AppLayout>
  );
};

export default withApollo()(carto);
